/**
 * Page Mes Réservations - Pour voir toutes ses réservations en tant que locataire
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  LinearProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Paper,
  Fade,
  Stack,
  Badge,
  useTheme,
  alpha,
  Modal,
  Backdrop,
  Snackbar,
  Fab,
  Drawer,
  keyframes,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  FilterList as FilterIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  QrCode as QrCodeIcon,
  QrCodeScanner as QrCodeScannerIcon,
  LocalShipping as LocalShippingIcon,
  ReportProblem as ReportIcon,
  CreditCard as CreditCardIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as RenterIcon,
  Home as OwnerIcon,
  SwapHoriz as SwapIcon,
  AccountCircle as AccountIcon,
  ShoppingCart as ShoppingCartIcon,
  HomeWork as HomeWorkIcon
} from '@mui/icons-material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { formatCurrency } from '../constants/dashboardConstants';
import { RESERVATION_STATUS } from '../constants/statusCodes';
import { 
  CANCELLATION_MESSAGES, 
  CANCELLATION_TIME_THRESHOLDS, 
  CANCELLATION_PENALTY_TYPE,
  CANCELLED_BY_FRONTEND,
  CACHE_KEYS 
} from '../constants/cancellationConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reservationService } from '../services/reservationService';
import CancellationService from '../services/cancellationService';
import MessagingInterface from '../components/MessagingInterface';
import ReservationDetailsPage from '../components/ReservationDetailsPage';
import MessageNotificationBadge from '../components/MessageNotificationBadge';
import useMessageNotifications from '../hooks/useMessageNotifications';
import QRCodeGeneratorModal from '../components/QRCodeGeneratorModal.js';
import QRCodeConfirmationModal from '../components/QRCodeConfirmationModal.js';
import QRCodeService from '../services/qrCodeService';
import { isDeliveryOrder } from '../utils/deliveryUtils';
import ProblemReportModal from '../components/ProblemReportModal.jsx';
import ProblemReportService from '../services/problemReportService.js';
import CancellationModalEnhanced from '../components/CancellationModalEnhanced';
import { toast } from 'react-toastify';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation } from 'react-i18next';
import { translateQRError, getQRSuccessMessage, getQRTechnicalError } from '../utils/qrTranslations';
import { DisputeActionBox } from '../components/disputes';
import disputeAdminService from '../services/disputeAdminService';

// Composants d'évaluation indépendants
import ReservationReviewButton from '../components/reviews/ReservationReviewButton';
import ReservationReviewModal from '../components/reviews/ReservationReviewModal';
import ReservationRatingDisplay from '../components/reviews/ReservationRatingDisplay';
import OwnerReviewDisplay from '../components/reviews/OwnerReviewDisplay';
import OwnerReviewForRenter from '../components/reviews/OwnerReviewForRenter';
import ModeratorNotesAccordion from '../components/ModeratorNotesAccordion';

// Modal de réputation
import UserReputationCard from '../components/reputation/UserReputationCard';

// Composant LoadingOverlay réutilisable
import LoadingOverlay from '../components/common/LoadingOverlay';
import ReservationsAdvancedFilters from '../components/reservation/ReservationsAdvancedFilters';

// Gradient moderne
const gradientStyle = {
  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
  color: 'white'
};

const MyReservationsPage = ({ embedded = false }) => {
  const { t } = useTranslation('dashboard');
  const { t: tProblemReport } = useTranslation('problemReport');
  const { showSuccess } = useErrorHandler();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  
  // États du sidebar (ajout)
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // États pour le modal de réputation


  // Animation de pulsation pour le FAB
  const pulseAnimation = keyframes`
    0% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
    50% {
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 12px 25px rgba(98, 0, 238, 0.5);
    }
    100% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
  `;

  const flameTrailAnimation = keyframes`
    0% {
      background-position: 0% 50%;
      opacity: 0.95;
      transform: translateX(0);
    }
    50% {
      background-position: 100% 50%;
      opacity: 0.6;
      transform: translateX(3px);
    }
    100% {
      background-position: 0% 50%;
      opacity: 0.95;
      transform: translateX(0);
    }
  `;

  // Gestion du sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Navigation vers la vue propriétaire
  const handleOwnerViewNavigation = () => {
    setSidebarOpen(false);
    navigate('/reservations-owner');
  };
  
  // Stabiliser user.id pour éviter les re-exécutions constantes
  const userIdRef = useRef(user?.id);
  const stableUserId = useMemo(() => {
    if (user?.id && user.id !== userIdRef.current) {
      userIdRef.current = user.id;
    }
    return userIdRef.current;
  }, [user?.id]);
  
  // Protection contre double appels API en développement (React.StrictMode)
  const apiCallsRef = useRef({
    fetchReservations: false,
    lastUserId: null
  });
  const cacheDisabledRef = useRef(false);
  // Ref pour mémoriser la réservation actuellement focalisée (pour restaurer le scroll)
  const prevFocusedReservationRef = useRef(null);
  
  const [reservations, setReservations] = useState([]);
  const [reservationDisputes, setReservationDisputes] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [temporalFilter, setTemporalFilter] = useState('all'); // 'all', 'upcoming', 'active', 'past'
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(false);
  const [filterHasUnreadMessages, setFilterHasUnreadMessages] = useState(false);
  const [filterHasOpenDispute, setFilterHasOpenDispute] = useState(false);
  const [filterNeedsReview, setFilterNeedsReview] = useState(false);
  const [filterCanBeCancelled, setFilterCanBeCancelled] = useState(false);
  const [filterQrRequired, setFilterQrRequired] = useState(false);
  const [sortByDate, setSortByDate] = useState('default');
  const [sortByAmount, setSortByAmount] = useState('none');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState([]);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Mapping fixe entre index d'onglet et paramètre de filtre
  const STATUS_PARAM = ['all', 'pending', 'confirmed', 'active', 'completed', 'cancelled'];

  // Filtres de période (création de réservation)
  const DATE_FILTERS = [
    { key: 'all', label: t('myReservations.filters.periods.all') },
    { key: 'today', label: t('myReservations.filters.periods.today') },
    { key: 'week', label: t('myReservations.filters.periods.week') },
    { key: 'month', label: t('myReservations.filters.periods.month') },
    { key: '3months', label: t('myReservations.filters.periods.3months') },
    { key: '6months', label: t('myReservations.filters.periods.6months') },
    { key: 'year', label: t('myReservations.filters.periods.year') }
  ];

  const [activeTab, setActiveTab] = useState(() => {
    try {
      // 1) Paramètre d'URL ?status=...
      const params = new URLSearchParams(window.location.search);
      const statusParam = (params.get('status') || '').toLowerCase();
      const idxFromUrl = STATUS_PARAM.indexOf(statusParam);
      if (idxFromUrl >= 0) return idxFromUrl;

      // 2) Fallback localStorage
      const saved = localStorage.getItem('myReservations_activeTab');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  // Configuration des éléments du sidebar pour les réservations
  const sidebarItems = [
    {
      title: t('sidebar.dashboard', 'Tableau de bord'),
      icon: <DashboardIcon />,
      action: () => {
        setSidebarOpen(false);
        navigate('/dashboard');
      },
      description: t('sidebar.dashboardDesc', 'Retour au tableau de bord principal')
    },
    { type: 'divider' },
    {
      title: t('sidebar.renterReservations', 'Mes Réservations'),
      icon: <RenterIcon />,
      action: () => setSidebarOpen(false),
      active: true,
      description: t('sidebar.renterReservationsDesc', 'Réservations en tant que locataire')
    },
    {
      title: t('sidebar.ownerReservations', 'Mes Locations'),
      icon: <OwnerIcon />,
      action: handleOwnerViewNavigation,
      active: false,
      description: t('sidebar.ownerReservationsDesc', 'Réservations en tant que propriétaire')
    }
  ];

  // Contenu du sidebar
  const SidebarContent = () => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header du sidebar avec gradient */}
      <Box
        sx={{
          ...gradientStyle,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={toggleSidebar}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Avatar
          src={user?.avatar}
          sx={{ width: 48, height: 48, border: '2px solid white' }}
        >
          <AccountIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {t('dashboard.reservations.title', 'Centre de Réservations')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
      </Box>

      {/* Menu du sidebar */}
      <Box sx={{ flex: 1, p: 2 }}>
        {sidebarItems.map((item, index) => {
          if (item.type === 'divider') {
            return <Divider key={index} sx={{ my: 1 }} />;
          }

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                mb: 1,
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                backgroundColor: item.active 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                borderLeft: item.active 
                  ? `4px solid ${theme.palette.primary.main}`
                  : '4px solid transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateX(4px)',
                  boxShadow: theme.shadows[2]
                }
              }}
              onClick={item.action}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    color: item.active ? theme.palette.primary.main : theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: item.active ? 600 : 400,
                      color: item.active ? theme.palette.primary.main : theme.palette.text.primary
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Footer du sidebar */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SwapIcon />}
          onClick={() => {
            setSidebarOpen(false);
            navigate('/dashboard');
          }}
          sx={{ borderRadius: 2 }}
        >
          {t('dashboard.sidebar.backToDashboard', 'Retour au Hub')}
        </Button>
      </Box>
    </Box>
  );
  const [dateFilter, setDateFilter] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = (params.get('period') || '').toLowerCase();
      const isValid = DATE_FILTERS.some(f => f.key === fromUrl);
      if (isValid) return fromUrl;
      return localStorage.getItem('myReservations_dateFilter') || 'all';
    } catch {
      return 'all';
    }
  });
  const [lastFetch, setLastFetch] = useState(null);
  const [showCancellationRulesModal, setShowCancellationRulesModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState(null);
  const [showMessaging, setShowMessaging] = useState(false);
  const [selectedReservationForChat, setSelectedReservationForChat] = useState(null);
  const [showReservationDetails, setShowReservationDetails] = useState(false);
  const [selectedReservationForDetails, setSelectedReservationForDetails] = useState(null);
  const [shouldScrollToTracking, setShouldScrollToTracking] = useState(false); // Flag pour scroll vers tracking
  
  // États QR Code
  const [qrGeneratorOpen, setQrGeneratorOpen] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrType, setQrType] = useState(''); // 'pickup' ou 'return'
  const [qrLoading, setQrLoading] = useState(false);
  const [qrReservation, setQrReservation] = useState(null);
  
  // États modal rapport de problème
  const [problemReportOpen, setProblemReportOpen] = useState(false);
  const [problemReportReservation, setProblemReportReservation] = useState(null);
  const [problemReportLoading, setProblemReportLoading] = useState(false);
  const [problemReportError, setProblemReportError] = useState('');
  
  // États modal évaluation
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewReservation, setReviewReservation] = useState(null);
  const [reviewEditMode, setReviewEditMode] = useState(false);
  const [reviewExistingData, setReviewExistingData] = useState(null);

  // Filtre type réservation : 'all' | 'listings' | 'products'
  const [reservationTypeFilter, setReservationTypeFilter] = useState('all');

  // Helper : détermine si une réservation est une commande produit
  const isProductReservation = (reservation) =>
    !!reservation?.listing?.booking_settings?.is_product_order;
  
  // Helper : retourne le lien correct pour afficher le détail (produit ou listing)
  const getLinkForReservation = (reservation) => {
    const isProd = isProductReservation(reservation);
    if (isProd && reservation?.listing?.product?.id) {
      return `/product/${reservation.listing.product.id}/order`;
    }
    // Default pour listings classiques
    const listingId = getListingData(reservation).id;
    return `/view-listing/${listingId}`;
  };
  
  // Rendu statique en mode embarqué (évite les animations et les effets visuels au chargement)
  const staticRender = embedded === true;

  // Petit helper pour désactiver Fade quand on est en mode embarqué
  const MaybeFade = ({ children, delay = 0 }) => (
    staticRender ? <>{children}</> : <Fade in timeout={300 + delay}>{children}</Fade>
  );

  // États pour les notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Hook pour les notifications de messages - pause les mises à jour quand des modaux sont ouverts
  const anyModalOpen = showMessaging || showReservationDetails || showCancelModal || 
                      qrGeneratorOpen || qrScannerOpen || problemReportOpen || showCancellationRulesModal;
  const { unreadCounts, hasUnreadMessages, getUnreadCount, markConversationAsRead, refreshCounts } = useMessageNotifications(anyModalOpen);
  // Synchroniser l'état sur changement d'URL externe (ex: navigation, rechargement)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = (params.get('status') || '').toLowerCase();
    const idxFromUrl = STATUS_PARAM.indexOf(statusParam);
    if (idxFromUrl >= 0 && idxFromUrl !== activeTab) {
      setActiveTab(idxFromUrl);
    }
    const periodParam = (params.get('period') || '').toLowerCase();
    if (periodParam && DATE_FILTERS.some(f => f.key === periodParam) && periodParam !== dateFilter) {
      setDateFilter(periodParam);
    }
  }, [location.search, activeTab, dateFilter]);

  // Gestion du changement d'onglet + persistance URL/localStorage
  const handleFilterChange = (newValue) => {
    setActiveTab(newValue);
    try {
      // Mettre à jour l'URL en préservant les autres paramètres (ex: tab=renter)
      const params = new URLSearchParams(location.search);
      params.set('status', STATUS_PARAM[newValue]);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      // Sauvegarde locale
      localStorage.setItem('myReservations_activeTab', String(newValue));
    } catch (e) {
      // ignore
    }
  };

  // Changement de période + persistance URL/localStorage
  const handleDateFilterChange = (newKey) => {
    setDateFilter(newKey);
    try {
      const params = new URLSearchParams(location.search);
      if (newKey === 'all') {
        params.delete('period');
      } else {
        params.set('period', newKey);
      }
      navigate(`${location.pathname}?${params.toString()}`,
        { replace: true }
      );
      localStorage.setItem('myReservations_dateFilter', newKey);
    } catch (e) {
      // ignore
    }
  };

  // Configuration du cache - uniquement pour les réservations locataire
  const CACHE_KEY = `${CACHE_KEYS.RENTER_RESERVATIONS}_${stableUserId}`;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

  // Fonction pour afficher les notifications
  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 🔧 Utilitaires de compression pour réduire la taille du cache
  const compressForCache = (data) => {
    // Garder seulement les champs essentiels pour réduire la taille de 60-70%
    return data.map(reservation => ({
      id: reservation.id,
      status: reservation.status,
      start_date: reservation.start_date,
      end_date: reservation.end_date,
      total_amount: reservation.total_amount,
      currency: reservation.currency,
      createdAt: reservation.createdAt,
      renter_id: reservation.renter_id,
      pickup_confirmed: reservation.pickup_confirmed,
      return_confirmed: reservation.return_confirmed,
      // Données listing compressées
      listing: reservation.listing ? {
        id: reservation.listing.id,
        title: reservation.listing.title,
        price_daily: reservation.listing.price_daily,
        price_hourly: reservation.listing.price_hourly,
        booking_mode: reservation.listing.booking_mode,
        booking_settings: reservation.listing.booking_settings,
        currency: reservation.listing.currency,
        mainImageUrl: reservation.listing.mainImageUrl,
        user_id: reservation.listing.user_id,
        // Images produit (premier élément seulement pour économiser de l'espace cache)
        product: reservation.listing.product ? {
          images: reservation.listing.product.images?.slice(0, 1) || []
        } : null,
        // Owner simplifié
        User: reservation.listing.User ? {
          id: reservation.listing.User.id,
          name: reservation.listing.User.name
        } : null
      } : null,
      // Reviews essentiels pour affichage
      canReviewListing: reservation.canReviewListing,
      hasReviewedListing: reservation.hasReviewedListing,
      canReviewOwner: reservation.canReviewOwner,
  hasReviewedOwner: reservation.hasReviewedOwner,
  ownerReviewForRenter: reservation.ownerReviewForRenter,
      // Inclure la review de la réservation pour conserver l'état d'évaluation
      reservationReview: reservation.reservationReview ? {
        id: reservation.reservationReview.id,
        overall_rating: reservation.reservationReview.overall_rating,
        overall_comment: reservation.reservationReview.overall_comment,
        moderation_status: reservation.reservationReview.moderation_status,
        moderator_notes: reservation.reservationReview.moderator_notes,
        moderator_id: reservation.reservationReview.moderator_id,
        reviewer_id: reservation.reservationReview.reviewer_id,
        listing_id: reservation.reservationReview.listing_id,
        reservation_id: reservation.reservationReview.reservation_id,
        experience_recommend: reservation.reservationReview.experience_recommend,
        equipment_quality_rating: reservation.reservationReview.equipment_quality_rating,
        equipment_quality_comment: reservation.reservationReview.equipment_quality_comment,
        equipment_cleanliness_rating: reservation.reservationReview.equipment_cleanliness_rating,
        equipment_cleanliness_comment: reservation.reservationReview.equipment_cleanliness_comment,
        equipment_state_rating: reservation.reservationReview.equipment_state_rating,
        equipment_state_comment: reservation.reservationReview.equipment_state_comment,
        process_pickup_rating: reservation.reservationReview.process_pickup_rating,
        process_pickup_comment: reservation.reservationReview.process_pickup_comment,
        process_return_rating: reservation.reservationReview.process_return_rating,
        process_return_comment: reservation.reservationReview.process_return_comment,
        process_communication_rating: reservation.reservationReview.process_communication_rating,
        process_communication_comment: reservation.reservationReview.process_communication_comment,
        experience_value_rating: reservation.reservationReview.experience_value_rating,
        experience_value_comment: reservation.reservationReview.experience_value_comment,
        positive_tags: reservation.reservationReview.positive_tags || [],
        createdAt: reservation.reservationReview.createdAt,
        updatedAt: reservation.reservationReview.updatedAt
      } : null,
      review_data: reservation.review_data || null
    }));
  };

  // 🧹 Rotation automatique du cache si quota dépassé
  const rotateOldCacheKeys = () => {
    try {
      const allKeys = Object.keys(localStorage);
      const reservationKeys = allKeys.filter(k => 
        k.startsWith('renter_reservations_') || k.startsWith('owner_reservations_')
      );
      
      // Trier par timestamp (les plus vieux en premier)
      const keysWithAge = reservationKeys.map(key => {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          return { key, timestamp: cached?.timestamp || 0 };
        } catch {
          return { key, timestamp: 0 };
        }
      }).sort((a, b) => a.timestamp - b.timestamp);
      
      // Supprimer les 3 plus vieilles clés
      const toRemove = keysWithAge.slice(0, Math.min(3, keysWithAge.length));
      toRemove.forEach(({ key }) => {
        localStorage.removeItem(key);
        console.log(`🧹 [cache] Clé supprimée: ${key}`);
      });
      
      return toRemove.length > 0;
    } catch (error) {
      console.error('[cache] Erreur rotation:', error);
      return false;
    }
  };

  // Fonctions de gestion du cache
  const getCachedData = () => {
    if (cacheDisabledRef.current) {
      console.log('⚠️ [cache] Cache désactivé (cacheDisabledRef=true)');
      return null;
    }
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) {
        console.log('ℹ️ [cache] Aucune donnée en cache');
        return null;
      }
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      const age = Math.round((now - timestamp) / 1000);
      
      // Vérifier si le cache est encore valide
      if (now - timestamp > CACHE_DURATION) {
        console.log(`⏰ [cache] Cache expiré (${age}s > ${CACHE_DURATION/1000}s)`);
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      
      console.log(`🚀 [cache] Données chargées depuis le cache (${data?.length || 0} réservations, âge: ${age}s)`);
      return data;
    } catch (error) {
      console.error('❌ [cache] Erreur lecture:', error);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCachedData = (data) => {
    if (cacheDisabledRef.current) {
      return;
    }
    try {
      // Compresser les données avant de les stocker
      const compressed = compressForCache(data);
      const cacheObject = {
        data: compressed,
        timestamp: Date.now()
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
      setLastFetch(Date.now());
      console.log(`💾 [cache] Données sauvegardées (${data.length} réservations compressées)`);
    } catch (error) {
      if (error?.name === 'QuotaExceededError') {
        console.warn('[cache] Quota atteint, tentative de rotation automatique...');
        
        // Essayer de faire de la place en supprimant les vieux caches
        const cleaned = rotateOldCacheKeys();
        
        if (cleaned) {
          // Réessayer après nettoyage
          try {
            const compressed = compressForCache(data);
            const cacheObject = {
              data: compressed,
              timestamp: Date.now()
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
            setLastFetch(Date.now());
            console.log('✅ [cache] Sauvegarde réussie après rotation');
            return;
          } catch (retryError) {
            console.warn('[cache] Échec après rotation, cache désactivé pour cette session');
          }
        }
        
        // Si échec, désactiver le cache pour cette session
        cacheDisabledRef.current = true;
        localStorage.removeItem(CACHE_KEY);
      } else {
        console.error('Erreur sauvegarde cache:', error);
      }
    }
  };

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY);
    setLastFetch(null);
    cacheDisabledRef.current = false;
  };

  // Fonction de récupération des réservations - stabilisée avec useCallback
  const fetchReservations = useCallback(async (forceRefresh = false) => {
      if (!stableUserId) {
        setError(t('myReservations.errors.userNotConnected'));
        setLoading(false);
        return;
      }

      // Protection contre double appels API avec debounce
      const now = Date.now();
      if (apiCallsRef.current.fetchReservations && 
          apiCallsRef.current.lastUserId === stableUserId && 
          !forceRefresh &&
          (now - (apiCallsRef.current.lastCallTime || 0)) < 500) {
        console.log('🚫 [fetchReservations] Appel API bloqué (debounce 500ms)');
        return;
      }

      apiCallsRef.current.fetchReservations = true;
      apiCallsRef.current.lastUserId = stableUserId;
      apiCallsRef.current.lastCallTime = now;

      // TEMP instrumentation pour mesurer le temps de chargement (à retirer après analyse)
      let requestStart = null;
      const requestMeta = { success: false, reservationCount: 0, error: null };

      // Charger depuis le cache d'abord (mode stale-while-revalidate)
      let cachedData = null;
      if (!forceRefresh) {
        cachedData = getCachedData();
        if (cachedData) {
          console.log(`✅ [cache] Affichage instantané du cache (${cachedData.length} réservations)`);
          setReservations(cachedData);
          setLoading(false);
          // 🔥 BUG FIX CRITIQUE: Charger les disputes même avec cache !
          console.log('📦 Chargement des disputes pour les réservations en cache...');
          fetchDisputesForReservations(cachedData); // Pas besoin d'await ici, on affiche le cache et on charge en background
        } else {
          console.log('🌐 [cache] Pas de cache valide, appel API nécessaire');
        }
      } else {
        console.log('🔄 [cache] forceRefresh=true, bypass du cache');
      }

      try {
        setLoading(true);
        setError(null);
        requestStart = performance.now();
          console.log('🔥 Récupération réservations LOCATAIRE depuis l\'API pour user:', stableUserId);
        
        // Pagination initiale : page 1
        const response = await reservationService.getUserReservations(stableUserId, {
          role: CANCELLED_BY_FRONTEND.RENTER,
          include: ['listing', 'listing.owner', 'listing.images'],
          limit: 10,
          page: 1,
          forceRefresh
        });
        const renterReservations = response?.data?.data?.reservations || [];
        setCachedData(renterReservations);
        setReservations(renterReservations);
        if (!cachedData) {
          setLoading(false);
        }
        setCurrentPage(1);
        setHasMore(renterReservations.length === 10);
        requestMeta.success = true;
        requestMeta.reservationCount = renterReservations.length;
        
        // Load disputes for these reservations
        await fetchDisputesForReservations(renterReservations);
      } catch (err) {
        console.error('Erreur récupération réservations:', err);
        setError(t('myReservations.errors.cannotLoad'));
        if (!cachedData) {
          setReservations([]);
        }
        requestMeta.error = err?.message || 'unknown error';
      } finally {
        setLoading(false);
        // Délai avant réinitialisation pour éviter les appels en rafale
        setTimeout(() => {
          apiCallsRef.current.fetchReservations = false;
        }, 500);
        if (requestStart !== null) {
          const duration = performance.now() - requestStart;
        }
      }
    }, [stableUserId, t]); // Dépendances stabilisées

  // Fetch disputes for reservations
  const fetchDisputesForReservations = useCallback(async (reservationsList) => {
    if (!reservationsList || reservationsList.length === 0) {
      return;
    }
    
    try {
      const disputeMap = {};
      
      // 🚀 OPTIMISATION: Batches de 50 réservations pour éviter la limite API
      const reservationIds = reservationsList.map(r => r.id);
      const BATCH_SIZE = 50;
      const batches = [];
      
      // Découper en batches de 50 IDs
      for (let i = 0; i < reservationIds.length; i += BATCH_SIZE) {
        batches.push(reservationIds.slice(i, i + BATCH_SIZE));
      }
      
      console.log(`📦 [fetchDisputesForReservations] ${reservationIds.length} réservations divisées en ${batches.length} batches de ${BATCH_SIZE}`);
      
      try {
        // Traiter chaque batch séquentiellement pour éviter de surcharger l'API
        for (const batch of batches) {
          const response = await disputeAdminService.list({
            reservation_ids: batch.join(','),
            limit: 100 // Largement suffisant pour 50 réservations
          });
        
          // Extraire les disputes de la réponse
          let disputes = [];
          if (Array.isArray(response)) {
            disputes = response;
          } else if (Array.isArray(response?.data)) {
            disputes = response.data;
          } else if (Array.isArray(response?.results)) {
            disputes = response.results;
          }
          
          // Grouper les disputes par reservation_id
          disputes.forEach(dispute => {
            const resId = dispute.reservation_id;
            if (resId && !disputeMap[resId]) {
              // Garder seulement le premier (le plus récent)
              disputeMap[resId] = dispute;
            }
          });
          
          console.log(`🚀 [Batch] ${disputes.length} disputes chargés pour ${batch.length} réservations`);
        }
        
        console.log(`✅ [fetchDisputesForReservations] Total: ${Object.keys(disputeMap).length} disputes pour ${reservationIds.length} réservations`);
      } catch (apiError) {
        // Fallback: Si l'API ne supporte pas les multiples IDs, faire des appels parallèles
        console.warn('⚠️ API groupée non supportée, fallback vers appels parallèles');
        
        const disputePromises = reservationsList.map(async (reservation) => {
          try {
            const response = await disputeAdminService.list({ reservation_id: reservation.id });
            let disputes = [];
            if (Array.isArray(response)) {
              disputes = response;
            } else if (Array.isArray(response?.data)) {
              disputes = response.data;
            } else if (Array.isArray(response?.results)) {
              disputes = response.results;
            }
            return { reservationId: reservation.id, disputes };
          } catch (err) {
            console.error(`Failed to fetch disputes for reservation ${reservation.id}:`, err);
            return { reservationId: reservation.id, disputes: [] };
          }
        });
        
        // ⚡ Exécuter tous les appels EN PARALLÈLE au lieu de séquentiellement
        const results = await Promise.all(disputePromises);
        
        results.forEach(({ reservationId, disputes }) => {
          if (disputes.length > 0) {
            disputeMap[reservationId] = disputes[0];
          }
        });
        
        console.log(`⚡ [fetchDisputesForReservations] ${results.length} appels parallèles effectués`);
      }
      
      // 🔥 CORRECTION: MERGER avec les disputes existants au lieu d'écraser
      setReservationDisputes(prev => {
        const merged = { ...prev, ...disputeMap };
        console.log(`✅ Disputes mergés: ${Object.keys(prev).length} existants + ${Object.keys(disputeMap).length} nouveaux = ${Object.keys(merged).length} total`);
        return merged;
      });
    } catch (err) {
      console.error('Error fetching disputes:', err);
    }
  }, []);

  // useEffect pour charger les réservations au montage du composant
  useEffect(() => {
    if (!stableUserId) return;
    fetchReservations();
  }, [stableUserId]);

  // Rafraîchissement qui conserve la pagination (ré-fetch pages 1..currentPage)
  const refreshLoadedReservationsPreservePagination = useCallback(async () => {
    if (!stableUserId) return null;

    const pagesToFetch = Math.max(1, currentPage || 1);
    try {
      const pagePromises = [];
      for (let page = 1; page <= pagesToFetch; page += 1) {
        pagePromises.push(
          reservationService.getUserReservations(stableUserId, {
            role: CANCELLED_BY_FRONTEND.RENTER,
            include: ['listing', 'listing.owner', 'listing.images'],
            limit: 10,
            page,
            forceRefresh: true
          })
        );
      }

      const responses = await Promise.all(pagePromises);
      const allReservations = responses.flatMap(r => r?.data?.data?.reservations || []);

      const byId = new Map();
      allReservations.forEach(r => {
        if (r?.id != null && !byId.has(r.id)) byId.set(r.id, r);
      });
      const mergedReservations = Array.from(byId.values());

      setReservations(mergedReservations);
      setCachedData(mergedReservations);

      const lastPageReservations = responses?.[responses.length - 1]?.data?.data?.reservations || [];
      setHasMore(lastPageReservations.length === 10);

      await fetchDisputesForReservations(mergedReservations);
      return mergedReservations;
    } catch (error) {
      console.error('Erreur lors du refresh avec pagination conservée:', error);
      return null;
    }
  }, [stableUserId, currentPage, fetchDisputesForReservations]);

  const handleGenerateQR = useCallback(async (reservationId, type) => {
    // Note: Ne pas mettre qrLoading ici car le modal gère son propre état generating
    try {
      const result = await QRCodeService.generateQRCode(reservationId, type);
      return result;
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error(t('myReservations.qr.generateError'));
      return { success: false, error: t('myReservations.qr.generateError') };
    }
  }, []);

  // Fonctions de fermeture des modaux QR stabilisées - DÉFINIES EN PREMIER
  const handleCloseQRGenerator = useCallback(() => {
    setQrGeneratorOpen(false);
    setQrReservation(null);
    setQrType('');
  }, []);

  const handleCloseQRScanner = useCallback(() => {
    setQrScannerOpen(false);
    setQrReservation(null);
    setQrType('');
  }, []);

  const handleConfirmation = useCallback(async (confirmationData) => {
    if (qrLoading) return; // Éviter les double clics
    
    setQrLoading(true);
    try {
      let result;
      if (qrType === 'pickup') {
        result = await QRCodeService.confirmPickup(qrReservation.id, confirmationData);
      } else {
        result = await QRCodeService.confirmReturn(qrReservation.id, confirmationData);
      }
      
      if (result.success) {
        toast.success(getQRSuccessMessage(qrType, t));

        const apiUpdatedReservation = result?.data?.reservation;
        
        // 🚀 Préserver la pagination: mise à jour locale au lieu d'un refetch global
        setReservations(prev =>
          prev.map(res => {
            if (res.id === qrReservation.id) {
              const updated = { ...res, ...(apiUpdatedReservation || {}) };
              if (qrType === 'pickup') {
                updated.pickup_confirmed = true;
                updated.status = RESERVATION_STATUS.ACTIVE;
              } else {
                updated.return_confirmed = true;
                updated.status = RESERVATION_STATUS.COMPLETED;
                updated.reviewEligibility = apiUpdatedReservation?.reviewEligibility ?? undefined;
              }
              return updated;
            }
            return res;
          })
        );
        
        // Fermer le modal proprement
        handleCloseQRScanner();
        
        // Invalider le cache pour garantir la fraîcheur au prochain chargement
        clearCache();
        
        // 🔥 Recharger les disputes pour cette réservation spécifique pour mettre à jour le card
        await fetchDisputesForReservations([{ id: qrReservation.id }]);
      } else {
        toast.error(translateQRError(result, t), {
          autoClose: 8000, // 8 secondes pour que l'utilisateur ait le temps de lire
          position: "top-center" // Position plus visible
        });
        
        // NE PAS fermer le modal pour les erreurs de validation
        // L'utilisateur peut réessayer avec un autre code
      }
    } catch (error) {
      console.error('Error confirming:', error);
      toast.error(getQRTechnicalError(t), {
        autoClose: 8000,
        position: "top-center"
      });
      
      // En cas d'erreur, fermer aussi le modal
      handleCloseQRScanner();
    } finally {
      setQrLoading(false);
    }
  }, [qrType, qrReservation?.id, qrLoading, handleCloseQRScanner, t, clearCache, fetchDisputesForReservations]);

  const openQRGenerator = useCallback((reservation, type) => {
    // Fermer tous les autres modaux QR d'abord
    setQrScannerOpen(false);
    
    // Définir les données du nouveau modal
    setQrReservation(reservation);
    setQrType(type);
    setQrGeneratorOpen(true);
  }, []);

  const openQRScanner = useCallback((reservation, type) => {
    // Fermer tous les autres modaux QR d'abord
    setQrGeneratorOpen(false);
    
    // Définir les données du nouveau modal
    setQrReservation(reservation);
    setQrType(type);
    setQrScannerOpen(true);
  }, []);

  // Fonction pour ouvrir le modal de rapport de problème
  const openProblemReport = useCallback((reservation) => {
    // Fermer tous les autres modaux d'abord
    setQrGeneratorOpen(false);
    setQrScannerOpen(false);
    
    // Réinitialiser l'erreur
    setProblemReportError('');
    
    // Définir les données du nouveau modal
    setProblemReportReservation(reservation);
    setProblemReportOpen(true);
  }, []);

  // Fonction de fermeture du modal de rapport de problème
  const handleCloseProblemReport = useCallback(() => {
    setProblemReportOpen(false);
    setProblemReportError(''); // Réinitialiser l'erreur
    setProblemReportReservation(null);
  }, []);

  // Fonction de rafraîchissement intelligente
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      clearCache(); // Vider le cache d'abord
      await refreshLoadedReservationsPreservePagination(); // Conserve la pagination
    } catch (error) {
      console.error('Erreur lors du refresh:', error);
      showNotification(t('myReservations.errors.refreshFailed'), 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // Fonction de rafraîchissement après erreur
  const handleErrorRefresh = async () => {
    setError(null);
    setLoading(true);
    try {
      clearCache(); // Nettoyer le cache
      await fetchReservations(true); // Force le rafraîchissement complet
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
      setError(t('myReservations.errors.cannotLoad'));
    }
  };

  // Fonction de soumission du rapport de problème
  const handleProblemReportSubmit = useCallback(async (reportData) => {
    setProblemReportLoading(true);
    setProblemReportError(''); // Réinitialiser l'erreur avant l'envoi
    
    try {
      // Utiliser le vrai service backend
      await ProblemReportService.createProblemReport(reportData);
      
      console.log('Problem report submitted successfully:', reportData);
      
      // Afficher un message de succès avec toast
      toast.success(tProblemReport('success.reportSubmitted'), {
        autoClose: 8000, // 8 secondes
        position: "top-center"
      });
      
      // Rafraîchir sans perdre la pagination
      await refreshLoadedReservationsPreservePagination();
    } catch (error) {
      console.error('Error submitting problem report:', error);
      
      // Extraire le message d'erreur traduit
      let errorMessage = tProblemReport('errors.defaultError');
      
      // Priorité 1: Message traduit dans response.data
      if (error.response && error.response.data) {
        errorMessage = error.response.data.error || 
                      error.response.data.message || 
                      error.response.data.msg || 
                      errorMessage;
      }
      // Priorité 2: Message d'erreur d'Axios en dernier recours
      else if (error.message) {
        errorMessage = error.message;
      }
      
      // Définir l'erreur dans l'état local pour l'affichage dans le modal
      setProblemReportError(errorMessage);
      
      // Afficher un toast avec le message d'erreur pendant 8 secondes
      toast.error(errorMessage, {
        autoClose: 8000, // 8 secondes pour que l'utilisateur ait le temps de lire
        position: "top-center" // Position plus visible
      });
      
      throw error; // Re-throw pour que le modal puisse gérer l'erreur
    } finally {
      setProblemReportLoading(false);
    }
  }, [refreshLoadedReservationsPreservePagination]);

  // Handlers pour l'évaluation
  const handleOpenReview = useCallback((reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir le modal d'évaluation
    prevFocusedReservationRef.current = reservation?.id || null;
    setReviewReservation(reservation);
    setReviewExistingData(null);
    setReviewEditMode(false);
    setReviewModalOpen(true);
  }, []);

  const handleEditReview = useCallback((reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir le modal d'édition d'évaluation
    prevFocusedReservationRef.current = reservation?.id || null;
    setReviewReservation(reservation);
    setReviewExistingData(reservation?.reservationReview || null);
    setReviewEditMode(true);
    setReviewModalOpen(true);
  }, []);

  const handleCloseReview = useCallback(() => {
    setReviewModalOpen(false);
    setReviewReservation(null);
    setReviewEditMode(false);
    setReviewExistingData(null);
    // Restaurer le focus/scroll sur la carte précédemment ouverte
    try {
      const prevId = prevFocusedReservationRef.current;
      if (prevId) {
        setTimeout(() => {
          const el = document.getElementById(`reservation-card-${prevId}`);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          prevFocusedReservationRef.current = null;
        }, 50);
      }
    } catch (err) {
      console.warn('Erreur restauration scroll après fermeture review modal', err);
    }
  }, []);

  const handleReviewSubmitted = useCallback(async (reservation) => {
    // Succès de l'évaluation
    showSuccess('success.submitted');
    
    // Rafraîchir sans perdre la pagination
    await refreshLoadedReservationsPreservePagination();
  }, [refreshLoadedReservationsPreservePagination, showSuccess]);

  // Handler optimisé pour création de dispute
  const handleDisputeCreated = useCallback(async (newDispute) => {
    console.log('🎯 [handleDisputeCreated] Nouveau dispute créé:', newDispute);
    
    // 1. Mettre à jour uniquement le mapping des disputes (pas de re-fetch complet)
    if (newDispute && newDispute.reservation_id) {
      setReservationDisputes(prev => ({
        ...prev,
        [newDispute.reservation_id]: newDispute
      }));
      console.log('✅ Dispute ajouté localement pour reservation:', newDispute.reservation_id);
    }
    
    // 2. Invalider le cache pour forcer un refresh au prochain chargement
    clearCache();
    
    // 3. Optionnel: Rafraîchir après un court délai si nécessaire
    // setTimeout(() => {
    //   fetchReservations(true);
    // }, 1000);
  }, [clearCache]);

  const getStatusChip = (status) => {
    const statusConfig = {
      [RESERVATION_STATUS.PENDING]: { 
        label: t('myReservations.status.pending'), 
        color: 'warning', 
        icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
        gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
      },
      [RESERVATION_STATUS.CONFIRMED]: { 
        label: t('myReservations.status.confirmed'), 
        color: 'info', 
        icon: <CheckIcon sx={{ fontSize: 16 }} />,
        gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
      },
      [RESERVATION_STATUS.ACTIVE]: { 
        label: t('myReservations.status.active'), 
        color: 'primary', 
        icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,
        gradient: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
      },
      [RESERVATION_STATUS.COMPLETED]: { 
        label: t('myReservations.status.completed'), 
        color: 'success', 
        icon: <CheckIcon sx={{ fontSize: 16 }} />,
        gradient: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)'
      },
      [RESERVATION_STATUS.CANCELLED]: { 
        label: t('myReservations.status.cancelled'), 
        color: 'error', 
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        gradient: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
      },
      [RESERVATION_STATUS.EXPIRED]: { 
        label: t('myReservations.status.expired'), 
        color: 'error', 
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        gradient: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
      }
    };
    
    const config = statusConfig[status] || statusConfig[RESERVATION_STATUS.PENDING];
    return (
      <Chip
        label={config.label}
        size="small"
        icon={config.icon}
        sx={{
          background: config.gradient,
          color: 'white',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 28,
          '& .MuiChip-label': {
            px: 1
          },
          '& .MuiChip-icon': {
            color: 'white'
          }
        }}
      />
    );
  };

  // Détermine si la réservation correspond au filtre de période
  const passesDateFilter = (reservation) => {
    if (!dateFilter || dateFilter === 'all') return true;
    const rawDate = reservation.createdAt || reservation.created_at || reservation.creationDate || reservation.created;
    if (!rawDate) return true; // si inconnu, on n'exclut pas
    const created = new Date(rawDate);
    if (Number.isNaN(created.getTime())) return true;
    const now = new Date();
    // Normaliser à la minute
    const msInDay = 24 * 60 * 60 * 1000;
    const sinceDays = (days) => new Date(now.getTime() - days * msInDay);
    let threshold;
    switch (dateFilter) {
      case 'today': {
        threshold = new Date(now);
        threshold.setHours(0, 0, 0, 0);
        return created >= threshold;
      }
      case 'week':
        threshold = sinceDays(7);
        break;
      case 'month':
        threshold = sinceDays(30);
        break;
      case '3months':
        threshold = sinceDays(90);
        break;
      case '6months':
        threshold = sinceDays(180);
        break;
      case 'year':
        threshold = sinceDays(365);
        break;
      default:
        return true;
    }
    return created >= threshold;
  };

  const filteredReservationsByStatus = useMemo(() => {
    const groups = {
      all: []
    };

    Object.values(RESERVATION_STATUS).forEach(statusValue => {
      groups[statusValue] = [];
    });

    reservations.forEach((reservation) => {
      if (!passesDateFilter(reservation)) {
        return;
      }

      groups.all.push(reservation);

      if (groups[reservation.status]) {
        groups[reservation.status].push(reservation);
      }
    });

    return groups;
  }, [reservations, dateFilter]);

  const filterReservations = (status) => {
    if (!filteredReservationsByStatus[status]) {
      return [];
    }
    return filteredReservationsByStatus[status];
  };

  // Fonction pour extraire les données du listing (support des deux formats)

  const getListingData = (reservation) => {
    // Support des deux formats: listing (correct) et Listing (ancien)
    const listing = reservation.listing || reservation.Listing;
    return {
      id: listing?.id || reservation.listing_id,
      title: listing?.title || 'Matériel non disponible',
      price_daily: listing?.price_daily || 0,
      price_hourly: listing?.price_hourly || 0,
      booking_mode: listing?.booking_mode || 'date_range',
      booking_settings: listing?.booking_settings || {},
      currency: listing?.currency,
      primary_image: listing?.mainImageUrl || listing?.product?.images?.[0] || listing?.images?.[0]?.url || listing?.images?.[0]?.image_url,
      owner: listing?.User || listing?.owner || listing?.Owner,
      address: listing?.address,
      city: listing?.city,
      postal_code: listing?.postal_code
    };
  };

  // Retourne { amount, label } pour l'affichage du prix unitaire sur la card
  const getListingUnitPrice = (listingData) => {
    const { booking_mode, booking_settings, price_daily, price_hourly } = listingData;
    const bs = booking_settings || {};

    // Prix de base dans booking_settings (créneaux)
    const slotPrice = parseFloat(bs.base_price || bs.price_per_slot || 0);
    const unit = bs.unit || 'day';

    if (booking_mode === 'time_slot' || booking_mode === 'time_slot_capacity') {
      if (slotPrice > 0) {
        const label = unit === 'hour' ? t('myReservations.pricing.perHour') : t('myReservations.pricing.perSlot');
        return { amount: slotPrice, label };
      }
      if (parseFloat(price_hourly) > 0) return { amount: parseFloat(price_hourly), label: t('myReservations.pricing.perHour') };
      return { amount: null, label: t('myReservations.pricing.perSlot') };
    }

    // date_range : nuit ou jour selon date_range_mode
    const isNightly = bs.date_range_mode === 'nights';
    const label = isNightly ? t('myReservations.pricing.perNight') : t('myReservations.pricing.perDay');
    if (parseFloat(price_daily) > 0) return { amount: parseFloat(price_daily), label };
    return { amount: null, label };
  };

  // Fonction pour déterminer la devise à utiliser
  const getReservationCurrency = (reservation) => {
    // Priorité: 1. Reservation.currency, 2. listing.currency, 3. EUR par défaut
    return reservation.currency || 
           reservation.listing?.currency || 
           reservation.Listing?.currency || 
           'EUR';
  };

  const canLaunchDispute = (reservation) => {
    if (!reservation) {
      return false;
    }
    const allowedStatuses = [
      RESERVATION_STATUS.CONFIRMED,
      RESERVATION_STATUS.ACTIVE,
      RESERVATION_STATUS.COMPLETED
    ];
    return allowedStatuses.includes(reservation.status);
  };

  // Fonction pour gérer l'annulation d'une réservation - VERSION ENRICHIE AVEC RAISONS
  const handleCancelReservation = async (reservationId) => {
    // Trouver la réservation pour la passer au modal
    const reservation = reservations.find(r => r.id === reservationId);
    setReservationToCancel(reservation);
    setShowCancelModal(true);
  };

  // Nouvelle fonction avec gestion des raisons d'annulation
  const handleCancellationWithReason = async (data) => {
    const { reason, analytics, reservation } = data;
    
    try {
      console.log('🚫 Annulation réservation avec raison:', {
        reservationId: reservation.id,
        reason,
        analytics
      });
      
      // Fermer le modal et réinitialiser
      setShowCancelModal(false);
      setReservationToCancel(null);
      
      // Afficher un loading ou état de traitement
      setLoading(true);
      
      // Appel du service d'annulation avec raison enrichie
      const result = await CancellationService.cancelReservation(reservation.id, {
        reason,
        cancelled_by: CANCELLED_BY_FRONTEND.RENTER,
        analytics_data: analytics, // 📊 Pour reporting business
        source_context: 'renter_list'
      });
      
      if (result.success) {
        console.log('✅ Annulation avec raison réussie:', result.data);
        
        // Actualiser la liste des réservations SANS recharger la page
        clearCache();
        
        // Recharger les données sans perdre la pagination
        await refreshLoadedReservationsPreservePagination();
        
        // Afficher un message de succès avec raison
        const penalty = result.data.penalty ? 
          t('myReservations.cancellation.successWithPenalty', { amount: result.data.penalty.amount }) : 
          t('myReservations.cancellation.successNoPenalty');
        const successMessage = t('myReservations.cancellation.success', { penalty });
        showNotification(successMessage, 'success');
        
      } else {
        console.error('❌ Erreur annulation:', result.error);
        const displayError = result.error_key ? t(result.error_key, result.error) : result.error;
        showNotification(t('myReservations.cancellation.error', { error: displayError }), 'error');
      }
      
    } catch (error) {
      console.error('❌ Erreur annulation réservation:', error.message);
      showNotification(t('myReservations.cancellation.error', { error: error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fonction legacy pour compatibilité (si besoin fallback)
  // Fonction pour charger plus de réservations (pagination) sans doublons
  const loadMoreReservations = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await reservationService.getUserReservations(user.id, {
        role: CANCELLED_BY_FRONTEND.RENTER,
        include: ['listing', 'listing.owner', 'listing.images'],
        limit: 10,
        page: nextPage
      });
      const newReservations = response?.data?.data?.reservations || [];
      console.log(`[LoadMore] page: ${nextPage}, reçues: ${newReservations.length}, ids:`, newReservations.map(r => r.id));
      
      // 🔥 CORRECTION CRITIQUE: Charger les disputes AVANT d'ajouter les réservations
      if (newReservations.length > 0) {
        console.log(`🔍 Chargement des disputes pour ${newReservations.length} nouvelles réservations AVANT setReservations`);
        await fetchDisputesForReservations(newReservations);
      }
      
      // Filtrer les doublons par id
      setReservations(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        const uniques = newReservations.filter(r => !existingIds.has(r.id));
        console.log(`[LoadMore] uniques ajoutées: ${uniques.length}, total après ajout: ${prev.length + uniques.length}`);
        if (newReservations.length < 10 || uniques.length === 0) {
          setHasMore(false);
          console.log('[LoadMore] hasMore désactivé');
        }
        return [...prev, ...uniques];
      });
      setCurrentPage(prev => prev + 1);
    } catch (err) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };
  const confirmCancelReservation = async () => {
    if (!reservationToCancel) return;
    
    try {
      console.log('🚫 Annulation réservation (mode legacy):', reservationToCancel.id);
      
      // Fermer le modal et réinitialiser
      setShowCancelModal(false);
      setReservationToCancel(null);
      
      // Afficher un loading ou état de traitement
      setLoading(true);
      
      // Appel du service d'annulation
      const result = await CancellationService.cancelReservation(reservationToCancel.id, {
        reason: CANCELLATION_MESSAGES.USER_CANCELLATION
      });
      
      if (result.success) {
        console.log('✅ Annulation réussie:', result.data);
        
        // Actualiser la liste des réservations SANS recharger la page
        clearCache();
        
        // Recharger les données sans perdre la pagination
        await refreshLoadedReservationsPreservePagination();
        
        // Afficher un message de succès
        const penalty = result.data.penalty ? 
          t('myReservations.cancellation.successWithPenalty', { amount: result.data.penalty.amount }) : 
          t('myReservations.cancellation.successNoPenalty');
        const successMessage = t('myReservations.cancellation.success', { penalty });
        showNotification(successMessage, 'success');
        
      } else {
        console.error('❌ Erreur annulation:', result.error);
        showNotification(t('myReservations.cancellation.error', { error: result.error }), 'error');
      }
      
    } catch (error) {
      console.error('❌ Erreur annulation réservation:', error.message);
      showNotification(t('myReservations.cancellation.error', { error: error.message }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    setReservationToCancel(null);
  };

  // Fonctions pour la messagerie
  const handleOpenMessaging = async (reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir le modal
    prevFocusedReservationRef.current = reservation?.id || null;
    setSelectedReservationForChat(reservation);
    setShowMessaging(true);

    // Marquer la conversation comme lue
    await markConversationAsRead(reservation.id);
  };

  const handleCloseMessaging = () => {
    setShowMessaging(false);
    setSelectedReservationForChat(null);
    // Restaurer le focus/scroll sur la carte précédemment ouverte
    try {
      const prevId = prevFocusedReservationRef.current;
      if (prevId) {
        setTimeout(() => {
          const el = document.getElementById(`reservation-card-${prevId}`);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          prevFocusedReservationRef.current = null;
        }, 50);
      }
    } catch (err) {
      console.warn('Erreur restauration scroll après fermeture messaging modal', err);
    }
  };

  // Fonctions pour les détails de réservation
  const handleViewDetails = (reservation) => {
    // Mémoriser la réservation focalisée avant d'ouvrir le modal
    prevFocusedReservationRef.current = reservation?.id || null;
    setSelectedReservationForDetails(reservation);
    setShowReservationDetails(true);
  };

  // Stabiliser la réservation sélectionnée pour éviter les re-renders du modal
  const stableSelectedReservation = useMemo(() => {
    if (!selectedReservationForDetails?.id) return null;
    
    // Chercher la réservation à jour dans la liste actuelle
    const updatedReservation = reservations.find(r => r.id === selectedReservationForDetails.id);
    return updatedReservation || selectedReservationForDetails;
  }, [selectedReservationForDetails?.id, reservations]);

  const handleCloseDetails = (hasDataChanged = false) => {
    setShowReservationDetails(false);
    setSelectedReservationForDetails(null);
    // Forcer le rafraîchissement si des modifications ont été effectuées
    if (hasDataChanged) {
      console.log('🔄 Rafraîchissement forcé après modification dans le modal de détails');
      refreshLoadedReservationsPreservePagination();
    }
    // Restaurer le focus/scroll sur la réservation affichée (si aucune modification nécessitant rafraîchissement)
    try {
      const prevId = prevFocusedReservationRef.current;
      if (prevId && !hasDataChanged) {
        setTimeout(() => {
          const el = document.getElementById(`reservation-card-${prevId}`);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          prevFocusedReservationRef.current = null;
        }, 50);
      } else {
        prevFocusedReservationRef.current = null;
      }
    } catch (err) {
      console.warn('Erreur restauration scroll après fermeture détails modal', err);
    }
  };

  // Fonction pour rafraîchir une réservation spécifique dans le modal
  const handleReservationRefresh = async (reservationId) => {
    try {
      console.log('🔄 Rafraîchissement réservation spécifique:', reservationId);
      
      clearCache();
      const refreshed = await refreshLoadedReservationsPreservePagination();
      const updatedReservation = (refreshed || reservations).find(r => r.id === reservationId);
      if (updatedReservation) {
        console.log('✅ Réservation mise à jour trouvée:', updatedReservation.status);
        setSelectedReservationForDetails(updatedReservation);
      } else {
        console.warn('⚠️ Réservation mise à jour non trouvée');
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement de la réservation:', error);
    }
  };

  // Fonction pour déterminer si une réservation peut être annulée - OPTIMISÉE
  const canCancelReservation = (reservation) => {
    // Utiliser le service local pour un calcul rapide
    return CancellationService.canCancelReservationLocally(reservation);
  };

  // Optimiser les calculs QR pour éviter les re-calculs constants
  const qrCapabilities = useMemo(() => {
    if (!stableUserId || !reservations.length) return {};
    
    console.log('🔧 [QR Capabilities] Calculating for', reservations.length, 'reservations, userId:', stableUserId, 'mount#:', Date.now());
    const capabilities = {};
    reservations.forEach(reservation => {
      capabilities[reservation.id] = {
        canScanReturn: QRCodeService.canScanQRCode(reservation, 'return', stableUserId),
        canScanPickup: QRCodeService.canScanQRCode(reservation, 'pickup', stableUserId)
      };
    });
    return capabilities;
  }, [reservations, stableUserId]);

  const getTabContent = () => {
    const tabs = ['all', RESERVATION_STATUS.PENDING, RESERVATION_STATUS.CONFIRMED, RESERVATION_STATUS.ACTIVE, RESERVATION_STATUS.COMPLETED, RESERVATION_STATUS.CANCELLED];
    const filteredReservations = filterReservations(tabs[activeTab]);

    const normalizeForSearch = (value) => {
      if (value === null || value === undefined) return '';
      return String(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    };

    const normalizedQuery = normalizeForSearch(searchQuery);

    // Get temporal category of a reservation
    const getTemporalCategory = (reservation) => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const startDate = reservation?.start_date ? new Date(reservation.start_date) : null;
      const endDate = reservation?.end_date ? new Date(reservation.end_date) : null;

      if (!startDate || !endDate) return 'all';

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (startDate > now) return 'upcoming';
      if (startDate <= now && endDate >= now) return 'active';
      if (endDate < now) return 'past';

      return 'all';
    };

    const matchesSearch = (reservation) => {
      if (!normalizedQuery) return true;

      const listing = getListingData(reservation);

      const ownerName =
        listing?.owner?.fullName ||
        listing?.owner?.name ||
        listing?.ownerName ||
        reservation?.owner?.fullName ||
        reservation?.owner?.name ||
        reservation?.Owner?.fullName ||
        reservation?.Owner?.name ||
        '';

      const listingTitle = listing?.title || listing?.name || reservation?.listingTitle || '';

      const city =
        listing?.city ||
        listing?.location?.city ||
        listing?.address?.city ||
        reservation?.city ||
        reservation?.location?.city ||
        reservation?.address?.city ||
        '';

      const address =
        listing?.address ||
        listing?.location?.address ||
        listing?.location?.formattedAddress ||
        listing?.fullAddress ||
        reservation?.address ||
        reservation?.location?.address ||
        reservation?.location?.formattedAddress ||
        '';

      const reservationId = reservation?.id || reservation?._id || reservation?.reservationId || '';

      const haystack = [listingTitle, ownerName, city, address, reservationId]
        .map(normalizeForSearch)
        .filter(Boolean)
        .join(' ');

      return haystack.includes(normalizedQuery);
    };

    const matchesTemporalFilter = (reservation) => {
      if (temporalFilter === 'all') return true;
      return getTemporalCategory(reservation) === temporalFilter;
    };

    const matchesDateRange = (reservation) => {
      if (!dateRangeStart && !dateRangeEnd) return true;

      const startDate = reservation?.start_date ? new Date(reservation.start_date) : null;
      const endDate = reservation?.end_date ? new Date(reservation.end_date) : null;

      if (!startDate || !endDate) return true;

      if (dateRangeStart) {
        const rangeStart = new Date(dateRangeStart);
        rangeStart.setHours(0, 0, 0, 0);
        if (endDate < rangeStart) return false;
      }

      if (dateRangeEnd) {
        const rangeEnd = new Date(dateRangeEnd);
        rangeEnd.setHours(23, 59, 59, 999);
        if (startDate > rangeEnd) return false;
      }

      return true;
    };

    const matchesUnreadMessages = (reservation) => {
      if (!filterHasUnreadMessages) return true;
      // Vérifier si la réservation a des messages non lus en utilisant getUnreadCount
      const unreadCount = getUnreadCount(reservation?.id) || 0;
      return unreadCount > 0;
    };

    const matchesOpenDispute = (reservation) => {
      if (!filterHasOpenDispute) return true;
      // Vérifier si la réservation a un litige ouvert
      return !!reservationDisputes[reservation?.id];
    };

    const matchesNeedsReview = (reservation) => {
      if (!filterNeedsReview) return true;
      // Vérifier si la réservation est complétée ET n'a pas de review
      // OU si la modération est en 'changes_requested'
      if (reservation?.status === 'completed') {
        const hasReview = reservation?.reviews && reservation.reviews.length > 0;
        const moderationStatus = reservation?.moderation_status;
        return !hasReview || moderationStatus === 'changes_requested';
      }
      return false;
    };

    const matchesCanBeCancelled = (reservation) => {
      if (!filterCanBeCancelled) return true;
      // Utiliser la vraie logique du CancellationService
      const result = CancellationService.canCancelReservationLocally(reservation);
      // Ne pas afficher si annulation impossible OU si litige ouvert
      const hasDispute = !!reservationDisputes[reservation?.id];
      return result.canCancel === true && !hasDispute;
    };

    const matchesQrRequired = (reservation) => {
      if (!filterQrRequired) return true;
      // Vérifier si pickup ou return QR sont requis
      // Pickup requis : si pickup_confirmed = false ET réservation est active ou prête à être active
      // Return requis : si pickup_confirmed = true ET return_confirmed = false AND réservation est active ou completed
      const pickupPending = !reservation?.pickup_confirmed && ['active', 'confirmed', 'pending'].includes(reservation?.status);
      const returnPending = reservation?.pickup_confirmed && !reservation?.return_confirmed && ['active', 'completed'].includes(reservation?.status);
      return pickupPending || returnPending;
    };

    const matchesPaymentMethod = (reservation) => {
      if (paymentMethodFilter.length === 0) return true;
      // Vérifier la méthode de paiement via les paiements associés
      if (!reservation?.payments || reservation.payments.length === 0) {
        return true; // Si pas de paiements, afficher la réservation
      }
      // Vérifier si au moins un paiement correspond au filtre
      return reservation.payments.some(payment => 
        paymentMethodFilter.includes(payment?.payment_method)
      );
    };

    let filteredBySearch = filteredReservations
      .filter(matchesSearch)
      .filter(matchesTemporalFilter)
      .filter(matchesDateRange)
      .filter(matchesUnreadMessages)
      .filter(matchesOpenDispute)
      .filter(matchesNeedsReview)
      .filter(matchesCanBeCancelled)
      .filter(matchesQrRequired)
      .filter(matchesPaymentMethod)
      .filter(r => {
        if (reservationTypeFilter === 'all') return true;
        const isProd = isProductReservation(r);
        return reservationTypeFilter === 'products' ? isProd : !isProd;
      });

    // Appliquer les tri
    const now = new Date();
    if (sortByDate === 'default') {
      // Tri par date de création (plus récente en premier)
      filteredBySearch.sort((a, b) => {
        const dateA = new Date(a?.createdAt || a?.created_at || a?.creationDate || new Date(0));
        const dateB = new Date(b?.createdAt || b?.created_at || b?.creationDate || new Date(0));
        return dateB - dateA;
      });
    } else if (sortByDate === 'newest') {
      filteredBySearch.sort((a, b) => {
        const dateA = new Date(a?.start_date) || new Date(0);
        const dateB = new Date(b?.start_date) || new Date(0);
        // Plus proche du maintenant en premier (plus récent = plus proche du now)
        const diffA = Math.abs(dateA - now);
        const diffB = Math.abs(dateB - now);
        return diffA - diffB;
      });
    } else if (sortByDate === 'oldest') {
      filteredBySearch.sort((a, b) => {
        const dateA = new Date(a?.start_date) || new Date(0);
        const dateB = new Date(b?.start_date) || new Date(0);
        // Plus éloigné du maintenant en premier (plus ancien = plus loin du now)
        const diffA = Math.abs(dateA - now);
        const diffB = Math.abs(dateB - now);
        return diffB - diffA;
      });
    }

    if (sortByAmount === 'asc') {
      filteredBySearch.sort((a, b) => {
        const amountA = parseFloat(a?.total_amount) || 0;
        const amountB = parseFloat(b?.total_amount) || 0;
        return amountA - amountB;
      });
    } else if (sortByAmount === 'desc') {
      filteredBySearch.sort((a, b) => {
        const amountA = parseFloat(a?.total_amount) || 0;
        const amountB = parseFloat(b?.total_amount) || 0;
        return amountB - amountA;
      });
    }
    
    if (filteredBySearch.length === 0) {
      return (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            color: theme.palette.text.secondary
          }}
        >
          <CalendarIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight="500">
            {t('myReservations.empty.title')}
          </Typography>
          <Typography variant="body1">
            {t('myReservations.empty.message')}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {filteredBySearch.map((reservation, index) => (
          <Grid item xs={12} key={reservation.id}>
            <MaybeFade delay={index * 100}>
              <Paper
                id={`reservation-card-${reservation.id}`}
                elevation={0}
                sx={{ 
                  p: 3,
                  pt: reservation.created_at ? 6 : 3,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.3s ease-in-out',
                  position: 'relative', // Pour positionner l'évaluation
                  '&:hover': { 
                    boxShadow: theme.shadows[8],
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {/* Date de création en haut à gauche */}
                {reservation.created_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ position: 'absolute', top: 12, left: 16 }}
                  >
                    {t('myReservations.createdAt', { date: new Date(reservation.created_at).toLocaleDateString() })}
                  </Typography>
                )}
                <Grid container spacing={3} alignItems="center">
                  {/* Image et infos du matériel - AMÉLIORÉ */}
                  <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      {/* Image du listing en miniature */}
                      <Avatar
                        src={
                          getListingData(reservation).primary_image ||
                          '/api/placeholder/80/80'
                        }
                        sx={{ 
                          width: 80, 
                          height: 80,
                          borderRadius: 2,
                          border: `2px solid ${theme.palette.divider}`,
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                        variant="rounded"
                        onClick={() => {
                          const url = getLinkForReservation(reservation);
                          const win = window.open(url, '_blank', 'noopener,noreferrer');
                          if (win) win.opener = null;
                        }}
                      />
                      
                      <Box sx={{ flex: 1 }}>
                        {/* Titre du listing */}
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          fontWeight="600"
                          sx={{ 
                            color: theme.palette.text.primary,
                            lineHeight: 1.3,
                            cursor: 'pointer',
                            '&:hover': {
                              color: theme.palette.primary.main
                            }
                          }}
                          onClick={() => {
                            const url = getLinkForReservation(reservation);
                            const win = window.open(url, '_blank', 'noopener,noreferrer');
                            if (win) win.opener = null;
                          }}
                        >
                          {getListingData(reservation).title}
                        </Typography>
                        
                        <Stack spacing={1}>
                          {/* Prix unitaire selon booking_mode (sans unité pour les produits) */}
                          {(() => {
                            const ld = getListingData(reservation);
                            const { amount, label } = getListingUnitPrice(ld);
                            const curr = ld.currency || getReservationCurrency(reservation);
                            const isProd = isProductReservation(reservation);
                            return (
                              <Typography
                                variant="body2"
                                fontWeight="600"
                                sx={{ color: theme.palette.success.main, fontSize: '0.9rem' }}
                              >
                                {amount != null
                                  ? isProd
                                    ? formatCurrency(amount, curr)
                                    : `${formatCurrency(amount, curr)} ${label}`
                                  : t('myReservations.pricing.priceNotDefined')
                                }
                              </Typography>
                            );
                          })()}
                          
                          {/* Nom du propriétaire - remplacé par UserReputationCard */}
                        </Stack>
                      </Box>
                    </Stack>

                    {/* UserReputationCard du propriétaire - sous la photo et le titre */}
                    {getListingData(reservation).owner?.id && (
                      <UserReputationCard
                        userId={getListingData(reservation).owner.id}
                        user={getListingData(reservation).owner}
                        fallbackName={t('myReservations.ownerFallback', 'Propriétaire')}
                        sx={{ mt: 1.5, width: '100%' }}
                      />
                    )}
                  </Grid>

                  {/* Dates et statut */}
                  <Grid item xs={12} md={3}>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {new Date(reservation.start_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                            {reservation.start_at && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              {new Date(reservation.start_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                              </Typography>
                            )}
                            {' — '}
                            {new Date(reservation.end_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                            {reservation.end_at && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                {new Date(reservation.end_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      {reservation.quantity_requested > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.quantityRequested')}:</Typography>
                          <Typography variant="caption" fontWeight="600">
                            {reservation.quantity_requested}
                             {/*{reservation.listing?.stock_quantity && ` / ${reservation.listing.stock_quantity}`}*/}
                          </Typography>
                        </Box>
                      )}
                      {reservation.seats_requested > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{t('reservationDetails.labels.seatsRequested')}:</Typography>
                          <Typography variant="caption" fontWeight="600">
                            {reservation.seats_requested}
                            {/*{reservation.listing?.capacity_default && ` / ${reservation.listing.capacity_default}`}*/}
                          </Typography>
                        </Box>
                      )}
                      {getStatusChip(reservation.status)}
                      
                      {/* Affichage de l'évaluation pour les réservations complétées */}
                      {reservation.status === RESERVATION_STATUS.COMPLETED && (
                        <Box sx={{ mt: 1 }}>

                          {/* Accordion des notes de modération */}
                          {reservation?.reservationReview?.moderation_status === 'changes_requested' && (
                            <Box sx={{ mt: 2 }}>
                              <ModeratorNotesAccordion 
                                moderatorNotes={reservation.reservationReview.moderator_notes}
                              />
                            </Box>
                          )}

                          {reservation?.reservationReview?.moderation_status === 'approved' ? (
                            <OwnerReviewDisplay reservation={reservation} userRole="renter" />
                          ) : (
                            <ReservationRatingDisplay reservation={reservation} showLabel={true} />
                          )}
                          
                          {/* Affichage de l'évaluation du propriétaire sur le locataire */}
                          <Box sx={{ mt: 2 }}>
                            <OwnerReviewForRenter reservation={reservation} />
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Grid>

                  {/* Prix moderne */}
                  <Grid item xs={12} md={2}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                      }}
                    >
                      <Stack alignItems="center" spacing={0.5}>
                        {
                          (() => {
                            const payments = reservation?.payments || [];
                            const methods = payments.map(p => (p?.payment_method || '').toLowerCase());
                            if (methods.some(m => m.includes('cash'))) {
                              return (
                                <Box sx={{ mb: 0.5 }}>
                                  <Box component="span" sx={{ fontSize: 14, lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box component="span">💰</Box>
                                    <Box component="span" sx={{ fontWeight: 700 }}>Cash</Box>
                                  </Box>
                                </Box>
                              );
                            }
                            if (methods.some(m => m.includes('stripe') || m.includes('card') || m.includes('credit'))) {
                              return (
                                <Box sx={{ mb: 0.5 }}>
                                  <Box component="span" sx={{ fontSize: 14, lineHeight: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box component="span">💳</Box>
                                    <Box component="span" sx={{ fontWeight: 700 }}>Card</Box>
                                  </Box>
                                </Box>
                              );
                            }
                            return (
                              <Box sx={{ mb: 0.5 }}>
                                <PaymentIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                              </Box>
                            );
                          })()
                        }
                        <Divider sx={{ width: '40%', my: 0.75 }} />
                        <Typography 
                          variant="h6" 
                          fontWeight="700"
                          color="success.main"
                          align="center"
                        >
                          {formatCurrency(
                            parseFloat(reservation.total_amount || 0), 
                            getReservationCurrency(reservation)
                          )}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          align="center"
                        >
                          {t('myReservations.total')}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Actions modernes */}
                  <Grid item xs={12} md={2}>
                    <Stack spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewDetails(reservation)}
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          minHeight: 32
                        }}
                      >
                        {t('myReservations.actions.details')}
                      </Button>
                      
                      {/* Bouton de messagerie - toujours disponible sauf pour les réservations annulées/expirées */}
                      {(reservation.status !== RESERVATION_STATUS.CANCELLED && 
                        reservation.status !== RESERVATION_STATUS.EXPIRED) && (
                        <MessageNotificationBadge
                          unreadCount={getUnreadCount(reservation.id)}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ChatIcon />}
                            color="primary"
                            onClick={() => handleOpenMessaging(reservation)}
                            fullWidth
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500,
                              minHeight: 32,
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                borderColor: theme.palette.primary.dark
                              }
                            }}
                          >
                            {t('myReservations.actions.message')}
                          </Button>
                        </MessageNotificationBadge>
                      )}
                      
                      {/* Boutons QR Code pour confirmations pickup/return OU Label livraison simple */}
                      {(reservation.status === RESERVATION_STATUS.CONFIRMED || reservation.status === RESERVATION_STATUS.ACTIVE) && (
                        <>
                          {isDeliveryOrder(reservation) ? (
                            /* Mode Livraison - Afficher label simple selon état */
                            !reservation.pickup_confirmed ? (
                              <Chip
                                icon={<LocalShippingIcon />}
                                label={t('delivery:awaitingShipmentFromSeller')}
                                size="medium"
                                color="warning"
                                variant="outlined"
                                sx={{
                                  fontWeight: 500,
                                  width: '100%',
                                  justifyContent: 'flex-start'
                                }}
                              />
                            ) : (
                              <Chip
                                icon={<CheckIcon />}
                                label={t('delivery:shipped')}
                                size="medium"
                                color="success"
                                variant="filled"
                                sx={{
                                  fontWeight: 500,
                                  width: '100%',
                                  justifyContent: 'flex-start'
                                }}
                              />
                            )
                          ) : (
                            /* Mode Pickup/Return classique - Afficher les boutons QR */
                            <>
                              {/* Bouton QR Pickup - visible pour le locataire si pickup pas confirmé */}
                              {QRCodeService.canGenerateQRCode(reservation, 'pickup', user?.id) && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<QrCodeIcon />}
                                  onClick={() => openQRGenerator(reservation, 'pickup')}
                                  disabled={qrGeneratorOpen || qrScannerOpen}
                                  fullWidth
                                  color="success"
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minHeight: 32
                                  }}
                                >
                                  {t('myReservations.actions.qrPickup')}
                                </Button>
                              )}
                              
                              {/* Bouton Scanner Return - visible pour le locataire si return pas confirmé et pickup fait */}
                              {qrCapabilities[reservation.id]?.canScanReturn && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<QrCodeScannerIcon />}
                                  onClick={() => openQRScanner(reservation, 'return')}
                                  disabled={qrLoading || qrGeneratorOpen || qrScannerOpen}
                                  fullWidth
                                  color="warning"
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    minHeight: 32
                                  }}
                                >
                                  {t('myReservations.actions.scanReturn')}
                                </Button>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {/* Bouton Signaler un problème - visible uniquement en ACTIVE pour le renter */}
                      {reservation.status === RESERVATION_STATUS.ACTIVE && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ReportIcon />}
                          onClick={() => openProblemReport(reservation)}
                          disabled={qrLoading || qrGeneratorOpen || qrScannerOpen}
                          fullWidth
                          color="error"
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            minHeight: 32,
                            borderColor: 'error.main',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              borderColor: 'error.dark'
                            }
                          }}
                        >
                          {t('myReservations.actions.reportProblem')}
                        </Button>
                      )}

                      {/* Nouvelle section Livraison - au-dessus des Disputes */}
                      {reservation.status === RESERVATION_STATUS.COMPLETED && 
                       isDeliveryOrder(reservation) && 
                       reservation.pickup_confirmed && (
                        <Box sx={{ mb: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            fullWidth
                            onClick={() => {
                              setShouldScrollToTracking(true);
                              handleViewDetails(reservation);
                            }}
                            startIcon={<ViewIcon />}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              mb: 1
                            }}
                          >
                            {t('delivery:viewTracking')}
                          </Button>
                          <Chip
                            icon={<CheckIcon />}
                            label={t('delivery:shipped')}
                            size="medium"
                            color="success"
                            variant="filled"
                            sx={{
                              width: '100%',
                              fontWeight: 500,
                              fontSize: '0.875rem'
                            }}
                          />
                        </Box>
                      )}

                      {(canLaunchDispute(reservation) || Boolean(reservationDisputes[reservation.id])) && (
                        <DisputeActionBox
                          reservation={reservation}
                          actorRole="renter"
                          fullWidth
                          disabled={loading || qrLoading || qrGeneratorOpen || qrScannerOpen}
                          existingDispute={reservationDisputes[reservation.id]}
                          onCreated={handleDisputeCreated}
                        />
                      )}
                      
                      {/* Ligne avec bouton Annuler et bouton Info */}
                      {(reservation.status === RESERVATION_STATUS.CONFIRMED || reservation.status === RESERVATION_STATUS.PENDING) && (() => {
                        const cancellationInfo = canCancelReservation(reservation);
                        const hasDispute = Boolean(reservationDisputes[reservation.id]);
                        const cancelTooltip = hasDispute
                          ? t('myReservations.cancelDisabledDueToDispute', "Impossible d'annuler : un litige est ouvert pour cette réservation")
                          : (cancellationInfo.canCancel ? `${cancellationInfo.reason} (${cancellationInfo.hoursBeforeStart}h restantes)` : cancellationInfo.reason);

                        return (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={cancelTooltip} arrow>
                              <span style={{ flex: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  color={hasDispute ? 'warning' : 'error'}
                                  disabled={!cancellationInfo.canCancel || hasDispute}
                                  onClick={() => handleCancelReservation(reservation.id)}
                                  fullWidth
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    opacity: (!cancellationInfo.canCancel || hasDispute) ? 0.6 : 1,
                                    minHeight: 32
                                  }}
                                >
                                  {t('myReservations.actions.cancel')}
                                </Button>
                              </span>
                            </Tooltip>
                            
                            {/* Bouton d'information rond */}
                            <Tooltip title={t('tooltips.cancellationRules')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => setShowCancellationRulesModal(true)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: cancellationInfo.canCancel 
                                    ? alpha(theme.palette.success.main, 0.2)
                                    : alpha(theme.palette.warning.main, 0.2),
                                  color: cancellationInfo.canCancel ? 'success.main' : 'warning.main',
                                  '&:hover': {
                                    backgroundColor: cancellationInfo.canCancel 
                                      ? alpha(theme.palette.success.main, 0.3)
                                      : alpha(theme.palette.warning.main, 0.3),
                                  }
                                }}
                              >
                                <InfoIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        );
                      })()}
                      
                      {/* Bouton d'évaluation - Ajout non-intrusif */}
                      {reservation?.reservationReview?.moderation_status === 'changes_requested' ? (
                        <Stack spacing={1.25} sx={{ width: '100%' }}>
                      
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditReview(reservation)}
                            disabled={loading || qrLoading || qrGeneratorOpen || qrScannerOpen}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              minHeight: 32
                            }}
                          >
                            {t('myReservations.reviews.changeRequested.cta', 'Modifier mon avis')}
                          </Button>
                        </Stack>
                      ) : (
                        <ReservationReviewButton
                          reservation={reservation}
                          onReviewClick={handleOpenReview}
                          disabled={loading || qrLoading || qrGeneratorOpen || qrScannerOpen}
                        />
                      )}
                    </Stack>
                  </Grid>
                </Grid>


              </Paper>
            </MaybeFade>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return (
      <>
        <LoadingOverlay 
          isOpen={true}
          title={t('loading.reservations')}
          subtitle={t('myReservations.subtitle')}
          primaryColor="#6200ee"
          accentColor="#03dac4"
          circleSize={70}
          showWarning={false}
        />
      </>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: theme.palette.grey[50],
        pt: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container maxWidth="sm">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 6, 
              borderRadius: 3, 
              textAlign: 'center',
              border: `1px solid ${theme.palette.error.main}`,
              backgroundColor: alpha(theme.palette.error.main, 0.05)
            }}
          >
            <CancelIcon 
              sx={{ 
                fontSize: 80, 
                color: theme.palette.error.main, 
                mb: 2,
                opacity: 0.7
              }} 
            />
            <Typography variant="h5" gutterBottom fontWeight="600">
              {t('myReservations.errors.loadingTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={handleErrorRefresh}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {t('myReservations.errors.retry')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Modal des règles d'annulation
  const CancellationRulesModal = () => (
    <Modal
      open={showCancellationRulesModal}
      onClose={() => {
        setShowCancellationRulesModal(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={showCancellationRulesModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: 800, lg: 900 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          {/* Header du modal */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight="600" color="primary">
              {t('myReservations.cancellation.rules.title')}
            </Typography>
            <IconButton 
              onClick={() => setShowCancellationRulesModal(false)}
              size="small"
              sx={{ 
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                '&:hover': { backgroundColor: alpha(theme.palette.grey[500], 0.2) }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* Contenu des règles */}
          <Grid container spacing={3}>
            {/* Règle 1: Annulation gratuite */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <CheckIcon sx={{ color: 'success.main', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight="600" color="success.main">
                    {t('myReservations.cancellation.rules.free.title')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('myReservations.cancellation.rules.free.description', { hours: CANCELLATION_TIME_THRESHOLDS.FREE_CANCELLATION_HOURS })}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block',
                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                  color: 'success.main',
                  p: 1,
                  borderRadius: 1,
                  fontWeight: 500
                }}>
                  ✓ {t('myReservations.cancellation.rules.free.benefit')}
                </Typography>
              </Paper>
            </Grid>

            {/* Règle 2: Annulation avec pénalité financière */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <MoneyIcon sx={{ color: 'warning.main', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight="600" color="warning.main">
                    {t('myReservations.cancellation.rules.penalty.title')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('myReservations.cancellation.rules.penalty.description', { hours: CANCELLATION_TIME_THRESHOLDS.FREE_CANCELLATION_HOURS })}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block',
                  backgroundColor: alpha(theme.palette.warning.main, 0.2),
                  color: 'warning.main',
                  p: 1,
                  borderRadius: 1,
                  fontWeight: 500
                }}>
                  ⚠️ {t('myReservations.cancellation.rules.penalty.benefit', { percentage: CANCELLATION_TIME_THRESHOLDS.PENALTY_PERCENTAGE })}
                </Typography>
              </Paper>
            </Grid>

            {/* Règle 3: Impact réputation */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <TrendingUpIcon sx={{ color: 'info.main', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight="600" color="info.main">
                    {t('myReservations.cancellation.rules.reputation.title')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('myReservations.cancellation.rules.reputation.description', { hours: CANCELLATION_TIME_THRESHOLDS.FREE_CANCELLATION_HOURS })}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block',
                  backgroundColor: alpha(theme.palette.info.main, 0.2),
                  color: 'info.main',
                  p: 1,
                  borderRadius: 1,
                  fontWeight: 500
                }}>
                  📊 {t('myReservations.cancellation.rules.reputation.benefit')}
                </Typography>
              </Paper>
            </Grid>

            {/* Règle 4: Annulation bloquée */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  borderRadius: 2,
                  height: '100%'
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <CancelIcon sx={{ color: 'error.main', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight="600" color="error.main">
                    {t('myReservations.cancellation.rules.blocked.title')}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('myReservations.cancellation.rules.blocked.description', { hours: CANCELLATION_TIME_THRESHOLDS.MINIMUM_CANCELLATION_HOURS })}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block',
                  backgroundColor: alpha(theme.palette.error.main, 0.2),
                  color: 'error.main',
                  p: 1,
                  borderRadius: 1,
                  fontWeight: 500
                }}>
                  🚫 {t('myReservations.cancellation.rules.blocked.benefit')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Footer du modal */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setShowCancellationRulesModal(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4
              }}
            >
              {t('myReservations.cancellation.rules.understood')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  // Wrapper conditionnel pour le mode embarqué
  const ContentWrapper = ({ children }) => {
    if (embedded) {
      return <Box sx={{ p: 0 }}>{children}</Box>;
    }
    return (
      <Box sx={{ 
        minHeight: '100vh',
        backgroundColor: theme.palette.grey[50],
        pt: 3
      }}>
        <Container maxWidth="xl">
          {children}
        </Container>
      </Box>
    );
  };

  return (
    <>
      {/* Composant LoadingOverlay réutilisable - avec traductions appliquées */}
      <LoadingOverlay 
        isOpen={refreshing}
        title={t('loading.reservations')}
        subtitle={t('loading.pleaseWait')}
        warning={t('loading.doNotClose')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
        circleSize={70}
        showWarning={true}
      />

      <ContentWrapper>
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          {/* Toggle Locations & Services / Produits */}
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: '4px',
                borderRadius: '12px',
                background: theme.palette.background.paper,
                border: '1px solid #e9e9e9',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
              }}
            >
              {[
                { value: 'all', label: t('search.type.both', 'Tout'), Icon: FilterIcon, color: theme.palette.text.primary },
                { value: 'listings', label: t('search.type.listings', 'Locations & Services'), Icon: HomeWorkIcon, color: theme.palette.primary.main },
                { value: 'products', label: t('search.type.products', 'Produits'), Icon: ShoppingCartIcon, color: theme.palette.success.main }
              ].map(({ value, label, Icon, color }) => {
                const active = reservationTypeFilter === value;
                return (
                  <Box
                    key={value}
                    onClick={() => setReservationTypeFilter(value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: { xs: 1.5, md: 2.5 },
                      py: 0.75,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1.5px solid transparent',
                      ...(active ? {
                        background: `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.2)} 100%)`,
                        borderColor: alpha(color, 0.35),
                        boxShadow: `0 2px 14px ${alpha(color, 0.18)}`
                      } : {
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.05),
                          borderColor: alpha(theme.palette.primary.main, 0.15)
                        }
                      })
                    }}
                  >
                    <Icon sx={{ fontSize: 16, color: active ? color : 'text.secondary', transition: 'color 0.2s' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: active ? 700 : 400,
                        color: active ? color : 'text.secondary',
                        fontSize: { xs: '0.75rem', md: '0.825rem' },
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ 
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Container maxWidth={false} sx={{ px: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => handleFilterChange(newValue)}
                    sx={{
                      '& .MuiTab-root': {
                        minHeight: 64,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        textTransform: 'none',
                        px: 3,
                        color: theme.palette.text.secondary,
                        '&.Mui-selected': {
                          color: theme.palette.primary.main,
                          fontWeight: 600
                        }
                      },
                      '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '3px 3px 0 0'
                      }
                    }}
                  >
                    <Tab 
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <FilterIcon fontSize="small" />
                          <span>{t('myReservations.filters.all')}</span>
                          <Chip 
                            label={filterReservations('all').length} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </Stack>
                      } 
                    />
                    <Tab 
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ScheduleIcon fontSize="small" />
                          <span>{t('myReservations.filters.pending')}</span>
                          <Chip 
                            label={filterReservations(RESERVATION_STATUS.PENDING).length} 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                          />
                        </Stack>
                      } 
                    />
                    <Tab 
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CheckIcon fontSize="small" />
                          <span>{t('myReservations.filters.confirmed')}</span>
                          <Chip 
                            label={filterReservations(RESERVATION_STATUS.CONFIRMED).length} 
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                        </Stack>
                      } 
                    />
                    <Tab 
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TrendingUpIcon fontSize="small" />
                          <span>{t('myReservations.filters.active')}</span>
                          <Chip 
                            label={filterReservations(RESERVATION_STATUS.ACTIVE).length} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </Stack>
                      } 
                    />
                    <Tab 
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CheckIcon fontSize="small" />
                          <span>{t('myReservations.filters.completed')}</span>
                          <Chip 
                            label={filterReservations(RESERVATION_STATUS.COMPLETED).length} 
                            size="small" 
                            color="success" 
                            variant="outlined"
                          />
                        </Stack>
                      } 
                    />
                    <Tab 
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CancelIcon fontSize="small" />
                          <span>{t('myReservations.filters.cancelled')}</span>
                          <Chip 
                            label={filterReservations(RESERVATION_STATUS.CANCELLED).length} 
                            size="small" 
                            color="error" 
                            variant="outlined"
                          />
                        </Stack>
                      } 
                    />
                  </Tabs>
                </Box>

                {/* Indicateur de mise à jour compact */}
                {lastFetch && (
                  <Chip
                    label={t('myReservations.updatedAgo', { minutes: Math.round((Date.now() - lastFetch) / 1000 / 60) })}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      height: 28,
                      '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5 }
                    }}
                  />
                )}
                
                {/* Bouton refresh compact */}
                <Tooltip title={t('myReservations.refresh')}>
                  <IconButton 
                    onClick={handleRefresh}
                    disabled={loading}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      width: 36,
                      height: 36,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2)
                      },
                      '&:disabled': {
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Liste déroulante Période */}
                <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 180 } }}>
                  <InputLabel id="period-label">{t('myReservations.filters.period')}</InputLabel>
                  <Select
                    labelId="period-label"
                    id="period-select"
                    value={dateFilter}
                    label={t('myReservations.filters.period')}
                    onChange={(e) => handleDateFilterChange(e.target.value)}
                  >
                    {DATE_FILTERS.map(opt => (
                      <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Container>
          </Box>

          {/* Filtres avancés - Section séparée sous les contrôles */}
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <ReservationsAdvancedFilters
              t={t}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              temporalFilter={temporalFilter}
              onTemporalFilterChange={setTemporalFilter}
              dateRangeStart={dateRangeStart}
              dateRangeEnd={dateRangeEnd}
              filtersExpanded={advancedFiltersExpanded}
              onFiltersExpandedChange={setAdvancedFiltersExpanded}
              hasUnreadMessages={filterHasUnreadMessages}
              onHasUnreadMessagesChange={setFilterHasUnreadMessages}
              hasOpenDispute={filterHasOpenDispute}
              onHasOpenDisputeChange={setFilterHasOpenDispute}
              needsReview={filterNeedsReview}
              onNeedsReviewChange={setFilterNeedsReview}
              canBeCancelled={filterCanBeCancelled}
              onCanBeCancelledChange={setFilterCanBeCancelled}
              qrRequired={filterQrRequired}
              onQrRequiredChange={setFilterQrRequired}
              sortByDate={sortByDate}
              onSortByDateChange={setSortByDate}
              sortByAmount={sortByAmount}
              onSortByAmountChange={setSortByAmount}
              paymentMethodFilter={paymentMethodFilter}
              onPaymentMethodFilterChange={setPaymentMethodFilter}
              onDateRangeChange={(start, end) => {
                setDateRangeStart(start);
                setDateRangeEnd(end);
              }}
              onReset={() => {
                setSearchQuery('');
              }}
            />
          </Box>
          
          <Box sx={{ p: 4 }}>
            {getTabContent()}
          </Box>
        </Paper>
      {/* ...existing code... */}
      {hasMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            {loadingMore && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: 0,
                  right: 0,
                  height: 6,
                  borderRadius: '999px',
                  background: 'linear-gradient(270deg, rgba(255,120,0,0.95), rgba(255,220,0,0.9), rgba(255,120,0,0.95))',
                  backgroundSize: '200% 100%',
                  animation: `${flameTrailAnimation} 1.6s ease-in-out infinite`,
                  filter: 'blur(1px)',
                  pointerEvents: 'none'
                }}
              />
            )}
            <Button
              type="button"
              onClick={loadMoreReservations}
              disabled={loadingMore}
              variant="contained"
              color="primary"
              sx={{
                animation: loadingMore ? 'none' : `${pulseAnimation} 1.2s ease-in-out infinite`,
                '&:disabled': {
                  animation: 'none'
                }
              }}
            >
              {loadingMore ? t('myReservations.loadMore.loading') : t('myReservations.loadMore.button')}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {t('myReservations.loadMore.allLoaded')}
          </Typography>
        </Box>
      )}
      {/* Actions rapides */}
      <Box mt={4} textAlign="center">
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/search')}
          sx={{ mr: 2 }}
        >
          {t('myReservations.actions.newReservation')}
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/dashboard')}
        >
          {t('myReservations.actions.backToDashboard')}
        </Button>
      </Box>

      {/* Modal d'annulation enrichi avec raisons */}
      <CancellationModalEnhanced
        open={showCancelModal}
        onClose={handleCancelModalClose}
        onConfirm={handleCancellationWithReason}
        title={t('modalTitle')}
        reservation={reservationToCancel}
        context="renter_cancellation"
        userRole="renter"
        loading={loading}
        showRules={true}
        showReservationDetails={true}
      />

      {/* Modal des règles d'annulation */}
      <CancellationRulesModal />

      {/* Modal de messagerie */}
      <Modal
        open={showMessaging}
        onClose={handleCloseMessaging}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showMessaging}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: 600 },
              height: { xs: '80vh', sm: '70vh', md: '600px' },
              maxWidth: 600,
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 0,
              overflow: 'hidden'
            }}
          >
            {selectedReservationForChat && (
              <MessagingInterface
                reservationId={selectedReservationForChat.id}
                reservation={selectedReservationForChat}
                currentUser={user}
                onClose={handleCloseMessaging}
              />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Modal de détails de réservation avec messagerie intégrée */}
      <Modal
        open={showReservationDetails}
        onClose={handleCloseDetails}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showReservationDetails}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '96%', md: '94%', lg: '92%', xl: '90%' },
              height: { xs: '95vh', sm: '92vh', md: '90vh', lg: '88vh', xl: '85vh' },
              maxWidth: 1600,
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 3,
              boxShadow: 24,
              p: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {stableSelectedReservation && (
              <ReservationDetailsPage
                reservation={stableSelectedReservation}
                currentUser={user}
                userRole="renter"
                scrollToTracking={shouldScrollToTracking}
                onBack={(hasChanges) => {
                  setShouldScrollToTracking(false); // Réinitialiser le flag
                  handleCloseDetails(hasChanges);
                }}
                onDataUpdate={async () => {
                  console.log('MyReservationsPage - onDataUpdate: Starting modal refresh...');
                  await refreshLoadedReservationsPreservePagination();
                  console.log('MyReservationsPage - onDataUpdate: Modal refresh completed');
                }} // Callback pour rafraîchir les données du modal
                onReservationRefresh={handleReservationRefresh} // Callback pour rafraîchir la réservation spécifique
              />
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modales QR Code - avec protection contre les rendus multiples */}
      {qrGeneratorOpen && qrReservation && qrType && !qrScannerOpen && (
        <QRCodeGeneratorModal
          key={`generator-${qrReservation.id}-${qrType}`}
          open={qrGeneratorOpen}
          onClose={handleCloseQRGenerator}
          reservation={qrReservation}
          type={qrType}
          onGenerate={handleGenerateQR}
        />
      )}

      {qrScannerOpen && qrReservation && qrType && !qrGeneratorOpen && (
        <QRCodeConfirmationModal
          key={`scanner-${qrReservation.id}-${qrType}`}
          open={qrScannerOpen}
          onClose={handleCloseQRScanner}
          reservation={qrReservation}
          type={qrType}
          expectedCode={qrType === 'pickup' ? qrReservation.client_code : qrReservation.owner_code}
          qrType={qrType}
          userRole="renter"
          onConfirm={handleConfirmation}
          onRefuse={() => console.log('Refus non disponible pour les renters')}
          loading={qrLoading}
        />
      )}

      {/* Modal de rapport de problème */}
      {problemReportOpen && problemReportReservation && (
        <ProblemReportModal
          key={`problem-report-${problemReportReservation.id}`}
          open={problemReportOpen}
          onClose={handleCloseProblemReport}
          reservation={problemReportReservation}
          onSubmit={handleProblemReportSubmit}
          loading={problemReportLoading}
          submitError={problemReportError}
        />
      )}
      
      {/* Drawer Sidebar - Seulement si pas embedded */}
      {!embedded && (
        <>
          <Drawer
            anchor="right"
            open={sidebarOpen}
            onClose={toggleSidebar}
            variant="temporary"
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: { xs: '100%', md: 350 },
                maxWidth: '100vw',
                height: '100%',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
              },
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(4px)'
              }
            }}
          >
            <SidebarContent />
          </Drawer>

          {/* FAB flottant pour ouvrir le sidebar */}
          {!sidebarOpen && (
            <Tooltip title={t('sidebar.open', 'Ouvrir le menu réservations')} placement="left">
              <Fab
                color="primary"
                sx={{
                  position: 'fixed',
                  top: '50%',
                  right: 24,
                  transform: 'translateY(-50%)',
                  zIndex: 1301,
                  animation: `${pulseAnimation} 2s ease-in-out infinite`,
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  '&:hover': {
                    animation: 'none',
                    transform: 'translateY(-50%) scale(1.15)',
                    boxShadow: '0 12px 25px rgba(98, 0, 238, 0.6)',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(45deg, #6200ee 40%, #03dac4 80%)'
                  },
                  '&:active': {
                    transform: 'translateY(-50%) scale(0.95)',
                    transition: 'all 0.1s ease'
                  }
                }}
                onClick={toggleSidebar}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </Fab>
            </Tooltip>
          )}
        </>
      )}
      
      {/* Modal d'évaluation - Ajout non-intrusif */}
      <ReservationReviewModal
        open={reviewModalOpen}
        onClose={handleCloseReview}
        reservation={reviewReservation}
        existingReview={reviewExistingData}
        editMode={reviewEditMode}
        onReviewSubmitted={handleReviewSubmitted}
      />
      
      {/* Fin ContentWrapper */}
      
      </ContentWrapper>
    </>
  );
};

export default MyReservationsPage;
